import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { Send as SendIcon, CheckCircle, AlertCircle } from 'lucide-react';

export default function FeedbackView() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState<'bug' | 'feature' | 'improvement' | 'other'>('other');
  const [message, setMessage] = useState('');
  const [subscribe, setSubscribe] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate form
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      // Simulate sending feedback (in a real app, this would call an API)
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      // Store feedback in localStorage as a demo
      const feedback = {
        name,
        email,
        category,
        message,
        subscribe,
        timestamp: new Date().toISOString(),
      };
      const allFeedback = JSON.parse(localStorage.getItem('appFeedback') || '[]');
      allFeedback.push(feedback);
      localStorage.setItem('appFeedback', JSON.stringify(allFeedback));

      setSubmitted(true);
      setName('');
      setEmail('');
      setMessage('');
      setSubscribe(false);
      setCategory('other');

      // Reset success message after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: 'calc(100vh - 90px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        px: 3,
        py: 2,
      }}
    >
      {/* Header */}
      <Typography variant="h5" fontWeight={700} mb={3}>
        Send Feedback
      </Typography>

      {/* Success Message */}
      {submitted && (
        <Alert
          severity="success"
          icon={<CheckCircle size={20} />}
          onClose={() => setSubmitted(false)}
          sx={{ mb: 2 }}
        >
          Thank you for your feedback! We appreciate your input and will review it shortly.
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" icon={<AlertCircle size={20} />} onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Feedback Form */}
      <Card variant="outlined" sx={{ maxWidth: 600 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            We value your feedback! Let us know how we can improve Project Manager. Whether it&apos;s a bug
            report, feature request, or general improvement suggestion, we&apos;d love to hear from you.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Name */}
            <TextField
              label="Your Name"
              variant="outlined"
              size="small"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              required
            />

            {/* Email */}
            <TextField
              label="Email Address"
              variant="outlined"
              size="small"
              fullWidth
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
            />

            {/* Category */}
            <TextField
              select
              label="Feedback Category"
              variant="outlined"
              size="small"
              fullWidth
              value={category}
              onChange={(e) => setCategory(e.target.value as any)}
              disabled={loading}
              SelectProps={{
                native: true,
              }}
            >
              <option value="bug">🐛 Bug Report</option>
              <option value="feature">✨ Feature Request</option>
              <option value="improvement">📈 Improvement</option>
              <option value="other">💬 Other</option>
            </TextField>

            {/* Message */}
            <TextField
              label="Your Feedback"
              variant="outlined"
              fullWidth
              multiline
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading}
              placeholder="Tell us what you think..."
              required
            />

            {/* Subscribe Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={subscribe}
                  onChange={(e) => setSubscribe(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Keep me updated about improvements related to my feedback"
            />

            {/* Submit Button */}
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon size={18} />}
              onClick={handleSubmit}
              disabled={loading || !name.trim() || !email.trim() || !message.trim()}
              sx={{
                backgroundColor: '#C9A84C',
                color: '#000',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { backgroundColor: '#b8983e' },
              }}
            >
              {loading ? 'Sending…' : 'Send Feedback'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* FAQ / Tips */}
      <Card variant="outlined" sx={{ maxWidth: 600, mt: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>
            Quick Tips
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0, '& li': { mb: 1 } }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Be as specific as possible when reporting issues
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Include steps to reproduce bugs if applicable
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Feature requests should explain the problem they solve
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Check if your feedback has already been submitted
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
